import { Button, Flex, Group, Modal, Pagination } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconPlus, IconUpload } from '@tabler/icons-react'
import { useCallback, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { Outlet, useSearchParams } from 'react-router'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import { useGetCameraList } from '@/queries/cameraQueries'
import { useGetDeviceList } from '@/queries/deviceQueries'
import { CrudAction, Resource } from '@/types/permissions'
import { getPageCount, getPageOffset } from '@/utils/pagination'
import { buildSortParams } from '@/utils/sorting'
import { FixedPageTitle } from '../layout/PageWithFixedToolbar/FixedPageTitle'
import { PageWithFixedToolbar } from '../layout/PageWithFixedToolbar/PageWithFixedToolbar'
import { useTableState } from '../ui-shared/Tables/useTableState'
import { AddCameraHandler } from './AddCameraHandler'
import { CameraList, TableSortConfig } from './CameraList/CameraList'
import { CameraListFilterConfig } from './CameraList/CameraListFilter/CameraListFilter'
import { ImportCompletionStatus } from './ImportCameras/milestone/ImportProgressHandler/ImportProgressHandler'
import { MilestoneImportHandler } from './ImportCameras/milestone/MilestoneImportHandler'

const PAGE_SIZE = 10

const DEFAULT_SORT_CONFIG: TableSortConfig = {
  key: 'camera_name',
  order: 'asc'
}

const DEFAULT_FILTER_CONFIG: CameraListFilterConfig = {
  device_id: '',
  status: '',
  text_search: ''
}

export const CameraListPage = () => {
  const [canCreateCamera] = useHasPermissions(Resource.Cameras, [
    CrudAction.Create
  ])

  const [
    isAddCameraModalOpened,
    { open: openAddCameraModal, close: closeAddCameraModal }
  ] = useDisclosure(false)

  const [
    isImportCameraModalOpened,
    { open: openImportCameraModal, close: closeImportCameraModal }
  ] = useDisclosure(false)

  const [tableState, setTableState] = useTableState({
    activePage: 1,
    sortConfig: DEFAULT_SORT_CONFIG,
    filterConfig: DEFAULT_FILTER_CONFIG
  })

  const { activePage, sortConfig } = tableState
  const [searchParams] = useSearchParams()
  const filterConfig: CameraListFilterConfig = {
    ...tableState.filterConfig,
    text_search: searchParams.get('text_search') ?? ''
  }

  const {
    data: cameraData,
    isFetching,
    isLoading,
    isError,
    refetch
  } = useGetCameraList(
    {
      offset: getPageOffset(activePage, PAGE_SIZE),
      limit: PAGE_SIZE,
      ordering: buildSortParams(sortConfig).ordering,
      device_id: filterConfig.device_id,
      status: filterConfig.status,
      text_search: filterConfig.text_search
    },
    {
      keepPreviousData: true
    }
  )

  const { data: deviceData } = useGetDeviceList({
    limit: 9999, // fetch all devices
    ordering: buildSortParams({
      key: 'label',
      order: 'asc'
    }).ordering
  })

  const cameras = cameraData?.results || []
  const devices = deviceData?.results || []

  const totalPageCount = getPageCount(cameraData?.count || 0, PAGE_SIZE)

  useEffect(() => {
    if (totalPageCount > 0 && activePage > totalPageCount) {
      setTableState({ activePage: totalPageCount })
    }
  }, [activePage, totalPageCount, setTableState])

  const handleCameraCreated = () => {
    closeAddCameraModal()

    setTableState({
      activePage: 1,
      sortConfig: DEFAULT_SORT_CONFIG,
      filterConfig: DEFAULT_FILTER_CONFIG
    })

    void refetch()
  }

  const handleImportCompleted = useCallback(
    (status: ImportCompletionStatus) => {
      if (status === 'success') {
        closeImportCameraModal()
      }

      setTableState({
        activePage: 1,
        sortConfig: DEFAULT_SORT_CONFIG,
        filterConfig: DEFAULT_FILTER_CONFIG
      })

      void refetch()
    },
    [closeImportCameraModal, setTableState, refetch]
  )

  return (
    <PageWithFixedToolbar
      toolbar={
        <FixedPageTitle
          title={<FormattedMessage id="cameras.list.title" />}
          leftSection={
            canCreateCamera && (
              <Group gap="xs" wrap="nowrap">
                <Button
                  size="xs"
                  miw={140}
                  leftSection={<IconPlus size={16} />}
                  onClick={openAddCameraModal}
                >
                  <FormattedMessage id="cameras.add" />
                </Button>

                <Button
                  size="xs"
                  miw={140}
                  variant="outline"
                  leftSection={<IconUpload size={16} />}
                  onClick={openImportCameraModal}
                >
                  <FormattedMessage id="cameras.import.fromMilestone" />
                </Button>
              </Group>
            )
          }
        />
      }
    >
      <CameraList
        cameras={cameras}
        sortConfig={sortConfig}
        filterConfig={filterConfig}
        isLoading={isLoading || isFetching}
        hasError={isError}
        onSort={(sortConfig) => setTableState({ activePage: 1, sortConfig })}
        onFilter={(filterConfig) =>
          setTableState({ activePage: 1, filterConfig })
        }
        onReload={() => void refetch()}
      />

      <Flex justify="end" mt="lg">
        <Pagination
          size="sm"
          total={totalPageCount}
          value={activePage}
          onChange={(value) => setTableState({ activePage: value })}
        />
      </Flex>

      <Modal
        size="xl"
        opened={isAddCameraModalOpened}
        title={<FormattedMessage id="cameras.add" />}
        onClose={closeAddCameraModal}
      >
        <AddCameraHandler
          devices={devices.filter((device) => device.active)}
          onCancel={closeAddCameraModal}
          onCameraCreated={handleCameraCreated}
        />
      </Modal>

      <Modal
        size="xl"
        opened={isImportCameraModalOpened}
        title={<FormattedMessage id="cameras.import.fromMilestone" />}
        onClose={closeImportCameraModal}
      >
        <MilestoneImportHandler
          devices={devices.filter((device) => device.active)}
          onCompletion={handleImportCompleted}
          onClose={closeImportCameraModal}
        />
      </Modal>

      <Outlet />
    </PageWithFixedToolbar>
  )
}
